import { IcsAttendee } from 'ts-ics'

import { Contact } from '@/shared/types/api'

import {
  extractCalendarParticipantKey,
  getCalendarEmailValue,
  hasValidCalendarEmail,
  isGuestScheduleAttendee,
  isGuestScheduleContact,
  isGuestScheduleParticipantId,
  isTelCalendarAttendee,
  normalizeCalendarEmail,
  toTelCalendarAttendeeEmail,
} from './lib'

export type CalendarAttendeeContactLookup = {
  currentUser?: Pick<Contact, 'guid' | 'email' | 'imLogin' | 'internalNumber' | 'name' | 'mobilePhone'> | null
  findByPServed: (pServed: string) => Contact | undefined
  findByInternalNumber: (number: string) => Contact | undefined
  findContactByMobilePhone: (phone: string) => Contact | undefined
  findContactByEmail: (email: string) => Contact | undefined
  findByGuid?: (guid: string) => Contact | undefined
  createExternalContact: (number: string, name?: string) => Contact
}

export type CalendarAttendeeDraft = {
  email: string
  name?: string
  dir?: string
  partstat: 'NEEDS-ACTION'
}

export const createEmailInviteeContact = (email: string, name?: string): Contact => ({
  name: name || email,
  email,
  id: email,
  internalNumber: '',
  mobilePhone: '',
  imLogin: '',
  isFavorite: false,
  groupIds: [],
  organization: null,
  position: null,
  terminalLogin: '',
  terminalPassword: '',
  bDay: '',
})

/**
 * Номер для ICS ATTENDEE / селектора: тот же выбор, что в чатах —
 * `mobilePhone && !imLogin ? mobilePhone : internalNumber`,
 * с запасным mobilePhone если внутреннего номера нет.
 * Иначе внешние с РТУ (номер в internalNumber) не попадают в событие.
 */
export const getScheduleContactPhone = (
  contact: Pick<Contact, 'imLogin' | 'internalNumber' | 'mobilePhone'>,
): string => {
  const mobile = contact.mobilePhone?.trim() ?? ''
  const internal = contact.internalNumber?.trim() ?? ''

  if (mobile && !contact.imLogin) {
    return mobile
  }

  return internal || mobile
}

const isCurrentUserParticipant = (
  participantId: string,
  currentUser?: CalendarAttendeeContactLookup['currentUser'],
): boolean => {
  if (!currentUser || !participantId) {
    return false
  }

  const key = extractCalendarParticipantKey(participantId)

  return participantId === currentUser.guid
    || participantId === currentUser.internalNumber
    || participantId === currentUser.imLogin
    || normalizeCalendarEmail(participantId) === normalizeCalendarEmail(currentUser.email)
    || Boolean(key && key === currentUser.internalNumber)
    || Boolean(currentUser.imLogin && key === extractCalendarParticipantKey(currentUser.imLogin))
}

const findKnownContact = (
  participantId: string,
  lookup: CalendarAttendeeContactLookup,
): Contact | undefined => {
  const key = extractCalendarParticipantKey(participantId)

  return lookup.findByGuid?.(participantId)
    ?? lookup.findByPServed(participantId)
    ?? (key ? lookup.findByInternalNumber(key) : undefined)
    ?? (key ? lookup.findContactByMobilePhone(key) : undefined)
    ?? (hasValidCalendarEmail(participantId) ? lookup.findContactByEmail(participantId) : undefined)
}

export const buildCalendarAttendeeFromParticipant = (
  participantId: string,
  lookup: CalendarAttendeeContactLookup,
): CalendarAttendeeDraft | undefined => {
  if (!participantId?.trim() || isGuestScheduleParticipantId(participantId)) {
    return undefined
  }

  if (isCurrentUserParticipant(participantId, lookup.currentUser) && lookup.currentUser) {
    const currentUser = lookup.currentUser
    if (hasValidCalendarEmail(currentUser.email)) {
      return {
        email: getCalendarEmailValue(currentUser.email),
        name: currentUser.name,
        dir: currentUser.imLogin,
        partstat: 'NEEDS-ACTION',
      }
    }

    const phone = (currentUser.mobilePhone || currentUser.internalNumber)?.trim()
    if (!phone) {
      return undefined
    }

    return {
      email: toTelCalendarAttendeeEmail(phone),
      name: currentUser.name || phone,
      dir: currentUser.imLogin,
      partstat: 'NEEDS-ACTION',
    }
  }

  const contact = findKnownContact(participantId, lookup)
  if (contact) {
    if (isGuestScheduleContact(contact)) {
      return undefined
    }

    if (hasValidCalendarEmail(contact.email)) {
      return {
        email: getCalendarEmailValue(contact.email),
        name: contact.name,
        ...(contact.imLogin ? { dir: contact.imLogin } : {}),
        partstat: 'NEEDS-ACTION',
      }
    }

    const phone = getScheduleContactPhone(contact)
    if (!phone) {
      return undefined
    }

    const phoneKey = extractCalendarParticipantKey(phone) || phone
    return {
      email: toTelCalendarAttendeeEmail(phoneKey),
      name: contact.name || phoneKey,
      ...(contact.imLogin ? { dir: contact.imLogin } : {}),
      partstat: 'NEEDS-ACTION',
    }
  }

  if (hasValidCalendarEmail(participantId)) {
    return {
      email: getCalendarEmailValue(participantId),
      name: participantId,
      partstat: 'NEEDS-ACTION',
    }
  }

  const key = extractCalendarParticipantKey(participantId)
  if (!key) {
    return undefined
  }

  return {
    email: toTelCalendarAttendeeEmail(key),
    name: key,
    partstat: 'NEEDS-ACTION',
  }
}

export const resolveCalendarAttendeeToContact = (
  attendee: IcsAttendee,
  lookup: CalendarAttendeeContactLookup,
): Contact | undefined => {
  const currentUser = lookup.currentUser as Contact | undefined

  if (attendee.dir && currentUser?.imLogin && attendee.dir === currentUser.imLogin) {
    return currentUser
  }

  if (attendee.dir) {
    const byDir = lookup.findByPServed(attendee.dir)
      ?? findKnownContact(attendee.dir, lookup)
    if (byDir) {
      return byDir
    }
  }

  const email = getCalendarEmailValue(attendee.email)
  if (email && !isTelCalendarAttendee(email)) {
    const byEmail = lookup.findContactByEmail(email)
      ?? (
        currentUser && normalizeCalendarEmail(currentUser.email) === normalizeCalendarEmail(email)
          ? currentUser
          : undefined
      )
    if (byEmail) {
      return byEmail
    }
    return createEmailInviteeContact(email, attendee.name)
  }

  const phone = extractCalendarParticipantKey(email) || attendee.name || ''
  if (!phone) {
    return undefined
  }

  const found = lookup.findByInternalNumber(phone)
    ?? lookup.findContactByMobilePhone(phone)
    ?? lookup.findByPServed(phone)
    ?? (currentUser && phone === currentUser.internalNumber ? currentUser : undefined)
  if (found) {
    return found
  }

  return lookup.createExternalContact(phone, attendee.name)
}

export const resolveCalendarAttendeesToContacts = (
  attendees: IcsAttendee[] | undefined,
  lookup: CalendarAttendeeContactLookup,
): Contact[] => {
  return (attendees ?? [])
    .filter((attendee) => !isGuestScheduleAttendee(attendee))
    .map((attendee) => resolveCalendarAttendeeToContact(attendee, lookup))
    .filter((contact): contact is Contact => Boolean(contact && !isGuestScheduleContact(contact)))
}
